import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useAppMessage } from '../../components/AppMessage';
import { Plus, Users, Loader2, Mail, Phone, Building2, Trash2, Upload, Download, Search, X, RotateCcw, RefreshCw, Bot, Zap, Edit, Clock, MessageSquare, Filter, CheckCircle, Check, ChevronsUpDown, UserPlus, ExternalLink, Lock, ListPlus } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import AdvancedDataGrid from '../../components/AdvancedDataGrid';
import CSVImportWizard from '../../components/CSVImportWizard';
import StatusSelect from '../../components/StatusSelect';
import SourceSelect from '../../components/SourceSelect';
import useLeadStatuses from '../../hooks/useLeadStatuses';
import { formatPhone } from '../../utils/formatPhone';
import LeadScoreBadge from '../../components/crm/LeadScoreBadge';
import { usePermissions, PERMISSIONS } from '../../hooks/usePermissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700' },
  { value: 'assigned', label: 'Assigned', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'qualified', label: 'Qualified', color: 'bg-purple-100 text-purple-700' },
  { value: 'closed_won', label: 'Closed Won', color: 'bg-green-100 text-green-700' },
  { value: 'closed_lost', label: 'Closed Lost', color: 'bg-red-100 text-red-700' },
];

const LEAD_TYPES = [
  { value: 'staffing_company', label: 'Staffing Company', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'client', label: 'Client', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'investor', label: 'Investor', color: 'bg-amber-100 text-amber-700' },
  { value: 'vendor', label: 'Vendor', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'franchise', label: 'Franchise', color: 'bg-purple-100 text-purple-700' },
  { value: 'misc', label: 'Misc', color: 'bg-slate-100 text-slate-700' },
];

const PREFIXES = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.'];
const ATS_OPTIONS = ['JobDiva', 'BullHorn', 'Salesforce', 'Greenhouse', 'Workday', 'iCIMS', 'Taleo'];
const AI_VOICE_OPTIONS = ['JobTalk', 'ConverzAI', 'HeyMilo', 'Glider', 'Paradox', 'HireVue'];

const initialFormData = {
  prefix: '', first_name: '', last_name: '', gender: '', title: '', company_id: '',
  location_id: '', work_email: '', personal_email: '', work_direct_phone: '',
  work_company_phone: '', mobile_phone: '', linkedin_url: '', source: '', status: 'new',
  description: '', dnc: false, dne: false, envelope_mailed: false,
  lead_types: ['staffing_company'], lead_type: 'crm',
  owner_id: '', owner_name: '',
};

function OwnerBadge({ ownerId, ownerName, status, currentUserId, onClaim, claiming }) {
  if (ownerId === currentUserId) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Mine</span>;
  }
  if (status === 'new' && !ownerId) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); onClaim(); }}
        disabled={claiming}
        className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium hover:bg-yellow-200 transition-colors disabled:opacity-50"
      >
        {claiming ? 'Claiming...' : 'Claim'}
      </button>
    );
  }
  if (ownerId) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{ownerName || 'Assigned'}</span>;
  }
  return <span className="text-slate-400">—</span>;
}

export default function LeadsPage() {
  const msg = useAppMessage();
  const navigate = useNavigate();
  const { hasPermission, isAdmin } = usePermissions();
  const { user: currentUser } = useAuth();
  const { allStatuses, addStatus } = useLeadStatuses();
  // Franchise-only users: have franchise.leads.read but NOT crm.leads.read (and not admin)
  const isFranchiseOnly = !isAdmin && hasPermission(PERMISSIONS.FRANCHISE_LEADS_READ) && !hasPermission(PERMISSIONS.CRM_LEADS_READ);
  const [leads, setLeads] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [total, setTotal] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAllPages, setSelectAllPages] = useState(false); // Select all across all pages
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const _lf = (() => { try { return JSON.parse(sessionStorage.getItem('jtcrm_leads_filters') || '{}'); } catch { return {}; } })();
  const [searchQuery, setSearchQuery] = useState(_lf.searchQuery || '');
  const [hideDNC, setHideDNC] = useState(_lf.hideDNC || false);
  const [selectedLeadTypes, setSelectedLeadTypes] = useState(_lf.selectedLeadTypes || []); // Multi-select filter for lead types
  const [selectedLists, setSelectedLists] = useState(_lf.selectedLists || []); // Multi-select filter for lists
  const [lists, setLists] = useState([]); // All available lists
  const [leadCategoryTab, setLeadCategoryTab] = useState(() => {
    // Franchise-only users default to 'all' — backend already filters to franchise leads
    if (!isAdmin && hasPermission(PERMISSIONS.FRANCHISE_LEADS_READ) && !hasPermission(PERMISSIONS.CRM_LEADS_READ)) {
      return 'all';
    }
    return localStorage.getItem('leadCategoryTab') || 'all';
  });
  const [leadTypeDropdownOpen, setLeadTypeDropdownOpen] = useState(false);
  const [listsDropdownOpen, setListsDropdownOpen] = useState(false);
  const [selectedSources, setSelectedSources] = useState(_lf.selectedSources || []);
  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false);
  const [scoreBandFilter, setScoreBandFilter] = useState(_lf.scoreBandFilter || '');
  const [scoreBands, setScoreBands] = useState({ hot: 0, warm: 0, cold: 0, inactive: 0 });
  const [columnPrefs, setColumnPrefs] = useState(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [formData, setFormData] = useState(() => ({ ...initialFormData, lead_types: isFranchiseOnly ? ['franchise'] : ['staffing_company'] }));
  const [saving, setSaving] = useState(false);
  
  // AI Call states
  const [aiCallDialogOpen, setAiCallDialogOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [aiCallLoading, setAiCallLoading] = useState(false);
  
  // Sequence Enrollment states
  const [sequenceDialogOpen, setSequenceDialogOpen] = useState(false);
  const [sequences, setSequences] = useState([]);
  const [selectedSequenceId, setSelectedSequenceId] = useState('');
  const [sequenceEnrollLoading, setSequenceEnrollLoading] = useState(false);
  
  // Bulk update state
  const [bulkUpdateDialogOpen, setBulkUpdateDialogOpen] = useState(false);
  const [bulkUpdateField, setBulkUpdateField] = useState('');
  const [bulkUpdateValue, setBulkUpdateValue] = useState('');
  const [bulkUpdateValues, setBulkUpdateValues] = useState([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  
  // Hover activities cache
  const [leadActivitiesCache, setLeadActivitiesCache] = useState({});
  
  // Filter visibility state
  const [showFilters, setShowFilters] = useState(true);

  // Claim ownership state
  const [claimingId, setClaimingId] = useState(null);

  const [viewFilter, setViewFilter] = useState(() => { try { return JSON.parse(localStorage.getItem('jtcrm_leads_filters') || '{}').viewFilter || 'mine'; } catch { return 'mine'; } }); // 'all' | 'new' | 'mine'

  // Company autocomplete state
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [newCompanyDialogOpen, setNewCompanyDialogOpen] = useState(false);
  const [newCompanyForm, setNewCompanyForm] = useState({ name: '', website: '', num_recruiters: '', num_reqs_open: '', geographies: '', years_in_business: '', revenues: '', ats_used: '', ai_voice_using: '', other_ai_tools: '', company_notes: '' });
  const [savingCompany, setSavingCompany] = useState(false);
  const [ownerSearchOpen, setOwnerSearchOpen] = useState(false);
  const [ownerSearch, setOwnerSearch] = useState('');
  
  // Create Email List from filtered leads
  const [emailListDialogOpen, setEmailListDialogOpen] = useState(false);
  const [emailListName, setEmailListName] = useState('');
  const [emailListDescription, setEmailListDescription] = useState('');
  const [emailListCreating, setEmailListCreating] = useState(false);

  // Grid-level filtered data (reflects column filters applied inside AdvancedDataGrid)
  const [gridFilteredData, setGridFilteredData] = useState(null);
  const handleGridFilteredDataChange = useCallback((data) => setGridFilteredData(data), []);

  // Permission checks — row-level via can_delete from API response
  const [selectedCompanyLocations, setSelectedCompanyLocations] = useState([]);
  
  // Cache reference — leads fetched once (all), tab filtering done client-side
  const cacheRef = useRef({
    leads: null,
    leadsTs: 0,
    users: null,
    companies: null,
    companiesTs: 0,
    lists: null,
    listsTs: 0,
    prefs: null,
  });
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Prevent double fetch
  const fetchedRef = useRef(false);

  // Single fetch on mount
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadData(false);
  }, []);
  
  // Close company suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showCompanySuggestions && !e.target.closest('.company-autocomplete')) {
        setShowCompanySuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCompanySuggestions]);
  
  const loadData = async (forceRefresh = false) => {
    const now = Date.now();
    const cache = cacheRef.current;

    // Per-resource freshness checks
    const leadsStale = forceRefresh || !cache.leads || (now - cache.leadsTs) >= CACHE_DURATION;
    const companiesStale = forceRefresh || !cache.companies || (now - cache.companiesTs) >= CACHE_DURATION;
    const listsStale = forceRefresh || cache.lists === null || (now - cache.listsTs) >= CACHE_DURATION;
    const usersStale = forceRefresh || !cache.users;
    const prefsNeeded = !cache.prefs;

    // Fully cached — apply immediately, no network calls
    if (!leadsStale && !companiesStale) {
      setLeads(cache.leads);
      setTotal(cache.leads.length);
      setUsers(cache.users || []);
      setCompanies(cache.companies);
      if (cache.lists !== null) setLists(cache.lists);
      if (cache.prefs) setColumnPrefs(cache.prefs);
      setLoading(false);
      setPrefsLoaded(true);
      return;
    }

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      // Always fetch ALL leads — tab filtering is done client-side for instant tab switching
      const [leadsRes, companiesRes, listsRes, prefsRes, usersRes] = await Promise.all([
        leadsStale ? axios.get(`${API}/leads?limit=1000`) : null,
        companiesStale ? axios.get(`${API}/companies?limit=1000`) : null,
        listsStale ? axios.get(`${API}/franchise/lists`).catch(() => ({ data: [] })) : null,
        prefsNeeded ? axios.get(`${API}/user/preferences/leads_columns`).catch(() => ({ data: {} })) : null,
        usersStale ? axios.get(`${API}/users/active-basic`).catch(() => ({ data: [] })) : null,
      ]);

      // Process leads
      if (leadsRes) {
        const leadsData = leadsRes.data?.items || [];
        setLeads(leadsData);
        setTotal(leadsData.length);
        setScoreBands(leadsRes.data?.score_bands || { hot: 0, warm: 0, cold: 0, inactive: 0 });
        cache.leads = leadsData;
        cache.leadsTs = Date.now();
      } else if (cache.leads) {
        setLeads(cache.leads);
        setTotal(cache.leads.length);
      }

      // Process users — from real API, not derived from leads
      if (usersRes) {
        const list = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.users || []);
        const usersData = list.filter(u => u.is_active !== false).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
        setUsers(usersData);
        cache.users = usersData;
      } else if (cache.users) {
        setUsers(cache.users);
      }

      // Process companies
      if (companiesRes) {
        const companiesData = companiesRes.data?.items || [];
        companiesData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCompanies(companiesData);
        cache.companies = companiesData;
        cache.companiesTs = Date.now();
      } else if (cache.companies) {
        setCompanies(cache.companies);
      }

      // Process lists
      if (listsRes) {
        const listsData = listsRes.data || [];
        setLists(listsData);
        cache.lists = listsData;
        cache.listsTs = Date.now();
      } else if (cache.lists !== null) {
        setLists(cache.lists);
      }

      // Process prefs
      if (prefsRes?.data?.value) {
        setColumnPrefs(prefsRes.data.value);
        cache.prefs = prefsRes.data.value;
      }

      if (forceRefresh) toast.success('Leads data refreshed');
    } catch (error) {
      console.error('[LeadsPage] Error loading data:', error);
      if (error.response?.status === 429) {
        toast.error('Too many requests. Please wait a moment and refresh.');
      } else if (error.response?.status === 401) {
        toast.error('Authentication error. Please log in again.');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view this page.');
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to load leads data';
        toast.error(errorMsg);
      }
      setLeads([]);
      setCompanies([]);
      setUsers([]);
      setLists([]);
      setTotal(0);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setPrefsLoaded(true);
    }
  };

  // Handle lead category tab change — instant, no network call (client-side filter)
  const handleLeadCategoryChange = (newTab) => {
    setLeadCategoryTab(newTab);
    localStorage.setItem('leadCategoryTab', newTab);
  };

  // Reset filters and show cached data
  const handleReset = () => {
    setSearchQuery('');
    setHideDNC(false);
    setSelectedIds(new Set());
    setSelectedLeadTypes([]);
    setSelectedLists([]);
    setSelectedSources([]);
    setLeadTypeDropdownOpen(false);
    setListsDropdownOpen(false);
    setSourceDropdownOpen(false);
    sessionStorage.removeItem('jtcrm_leads_filters');
    toast.success('Filters cleared');
  };

  // Persist filters to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('jtcrm_leads_filters', JSON.stringify({ searchQuery, hideDNC, selectedLeadTypes, selectedLists, selectedSources, scoreBandFilter }));
  }, [searchQuery, hideDNC, selectedLeadTypes, selectedLists, selectedSources, scoreBandFilter]);
  
  // Force refresh from database
  const handleRefresh = () => {
    loadData(true);
  };

  // Refresh data after mutations
  const refreshLeads = async () => {
    try {
      const res = await axios.get(`${API}/leads?limit=1000`);
      const leadsData = res.data.items || [];
      setLeads(leadsData);
      setTotal(leadsData.length);
      cacheRef.current.leads = leadsData;
      cacheRef.current.leadsTs = Date.now();
    } catch (error) {
      console.error('Refresh error:', error);
    }
  };

  const saveColumnPreferences = async (prefs) => {
    try {
      await axios.put(`${API}/user/preferences/leads_columns`, { value: prefs });
    } catch (error) {
      console.error('Failed to save column preferences');
    }
  };

  const handleColumnChange = (visibleColumns, columnOrder, columnWidths = {}) => {
    const prefs = { visibleColumns, columnOrder, columnWidths };
    setColumnPrefs(prefs);
    saveColumnPreferences(prefs);
  };

  const handleCompanyChange = (companyId) => {
    setFormData({ ...formData, company_id: companyId, location_id: '' });
    const company = companies.find(c => c.id === companyId);
    setSelectedCompanyLocations(company?.locations || []);
  };

  const handleCreateCompany = async () => {
    if (!newCompanyForm.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setSavingCompany(true);
    try {
      const payload = { ...newCompanyForm };
      // Clean empty strings to null
      Object.keys(payload).forEach(k => {
        if (k !== 'name' && payload[k] === '') payload[k] = null;
      });
      const res = await axios.post(`${API}/companies`, payload);
      const newCompany = { id: res.data.id, name: res.data.name || newCompanyForm.name.trim(), locations: [] };
      setCompanies(prev => [...prev, newCompany].sort((a, b) => a.name.localeCompare(b.name)));
      cacheRef.current.companies = null;
      setFormData(f => ({ ...f, company_id: newCompany.id, location_id: '' }));
      setCompanySearchQuery(newCompany.name);
      setSelectedCompanyLocations([]);
      setNewCompanyDialogOpen(false);
      setNewCompanyForm({ name: '', website: '', num_recruiters: '', num_reqs_open: '', geographies: '', years_in_business: '', revenues: '', ats_used: '', ai_voice_using: '', other_ai_tools: '', company_notes: '' });
      toast.success(`Company "${newCompany.name}" created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create company');
    } finally {
      setSavingCompany(false);
    }
  };

  const resetForm = () => {
    setFormData({ ...initialFormData, lead_types: isFranchiseOnly ? ['franchise'] : ['staffing_company'] });
    setSelectedCompanyLocations([]);
  };

  const handleCreate = async () => {
    if (!formData.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData };
      Object.keys(payload).forEach(k => {
        if (payload[k] === '' || payload[k] === 'none') payload[k] = null;
      });
      
      // Franchise users always create franchise leads; others default to staffing_company
      if (isFranchiseOnly) {
        payload.lead_types = ['franchise'];
      } else if (!payload.lead_types || payload.lead_types.length === 0) {
        payload.lead_types = ['staffing_company'];
      }
      
      await axios.post(`${API}/leads`, payload);
      toast.success('Lead created');
      setCreateDialogOpen(false);
      resetForm();
      refreshLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedLead) return;
    try {
      await axios.delete(`${API}/leads/${selectedLead.id}`);
      toast.success('Lead deleted');
      setDeleteDialogOpen(false);
      setSelectedLead(null);
      refreshLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete lead');
    }
  };

  const handleCreateEmailList = async () => {
    if (!emailListName.trim()) { toast.error('List name is required'); return; }
    setEmailListCreating(true);
    try {
      const leadIds = filteredLeadsWithEmail.map(l => l.id);
      const res = await axios.post(`${API}/email-lists/from-leads`, {
        lead_ids: leadIds,
        batch_name: emailListName.trim(),
        description: emailListDescription.trim(),
      });
      const d = res.data;
      const parts = [`${d.rows_imported} contacts imported`];
      if (d.rows_skipped_no_email) parts.push(`${d.rows_skipped_no_email} no email`);
      if (d.rows_skipped_duplicate) parts.push(`${d.rows_skipped_duplicate} duplicates`);
      if (d.rows_skipped_suppressed) parts.push(`${d.rows_skipped_suppressed} suppressed`);
      toast.success(`Created "${emailListName}" — ${parts.join(', ')}`);
      setEmailListDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create email list');
    } finally {
      setEmailListCreating(false);
    }
  };

  const claimLead = async (leadId) => {
    setClaimingId(leadId);
    try {
      await axios.put(`${API}/leads/${leadId}/claim`);
      toast.success('Lead claimed — you are now the owner');
      refreshLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim lead');
    } finally {
      setClaimingId(null);
    }
  };

  const handleBulkDelete = async () => {
    let leadsToDelete;
    
    // If select all pages is active, fetch all lead IDs
    if (selectAllPages) {
      try {
        const params = new URLSearchParams({
          limit: total.toString(),
          skip: '0'
        });
        if (searchQuery) params.append('search', searchQuery);
        if (selectedLeadTypes.length > 0) {
          selectedLeadTypes.forEach(type => params.append('lead_types', type));
        }
        
        const response = await axios.get(`${API}/leads?${params.toString()}`);
        leadsToDelete = response.data.items.map(lead => lead.id);
        
        if (!(await msg.confirm('Delete all leads?', `⚠️ WARNING: This will DELETE ALL ${leadsToDelete.length} leads across all pages. This action cannot be undone. Continue?`, { confirmLabel: 'Delete', destructive: true }))) {
          return;
        }
      } catch (error) {
        toast.error('Failed to fetch all leads');
        return;
      }
    } else {
      leadsToDelete = Array.from(selectedIds);
    }

    try {
      await Promise.all(leadsToDelete.map(id => axios.delete(`${API}/leads/${id}`)));
      toast.success(`${leadsToDelete.length} leads deleted`);
      setBulkDeleteDialogOpen(false);
      setSelectedIds(new Set());
      setSelectAllPages(false);
      refreshLeads();
    } catch (error) {
      toast.error('Failed to delete some leads');
    }
  };

  const handleBulkUpdate = async () => {
    if (!bulkUpdateField) {
      toast.error('Please select a field to update');
      return;
    }

    let selectedLeads;
    
    // If select all pages is active, fetch all lead IDs
    if (selectAllPages) {
      try {
        // Fetch all lead IDs with current filters
        const params = new URLSearchParams({
          limit: total.toString(),
          skip: '0'
        });
        if (searchQuery) params.append('search', searchQuery);
        if (selectedLeadTypes.length > 0) {
          selectedLeadTypes.forEach(type => params.append('lead_types', type));
        }
        
        const response = await axios.get(`${API}/leads?${params.toString()}`);
        selectedLeads = response.data.items.map(lead => lead.id);
        
        if (!(await msg.confirm('Update all leads?', `This will update ALL ${selectedLeads.length} leads across all pages. Continue?`))) {
          return;
        }
      } catch (error) {
        toast.error('Failed to fetch all leads');
        return;
      }
    } else {
      selectedLeads = Array.from(selectedIds);
      if (selectedLeads.length === 0) {
        toast.error('No leads selected');
        return;
      }
    }

    // Validate based on field type
    if (bulkUpdateField === 'lead_types' && bulkUpdateValues.length === 0) {
      toast.error('Please select at least one lead type');
      return;
    }
    if (bulkUpdateField === 'add_to_lists' && bulkUpdateValues.length === 0) {
      toast.error('Please select at least one list');
      return;
    }
    if (!['lead_types', 'add_to_lists'].includes(bulkUpdateField) && !bulkUpdateValue && bulkUpdateValue !== 'false') {
      toast.error('Please enter a value');
      return;
    }

    setBulkUpdating(true);
    try {
      let updateData = {};
      
      if (bulkUpdateField === 'lead_types') {
        updateData.lead_types = bulkUpdateValues;
      } else if (bulkUpdateField === 'add_to_lists') {
        // Add to lists - we'll need to do this individually
        const promises = selectedLeads.map(async (leadId) => {
          for (const listId of bulkUpdateValues) {
            await axios.post(`${API}/leads/${leadId}/add-to-list/${listId}`);
          }
        });
        await Promise.all(promises);
      } else if (['dnc', 'dne', 'envelope_mailed', 'bad_number'].includes(bulkUpdateField)) {
        // Boolean fields
        updateData[bulkUpdateField] = bulkUpdateValue === 'true';
      } else {
        // Text and other fields
        updateData[bulkUpdateField] = bulkUpdateValue;
      }

      // Update each selected lead (except for lists which we handled above)
      if (bulkUpdateField !== 'add_to_lists') {
        const promises = selectedLeads.map(leadId =>
          axios.put(`${API}/leads/${leadId}`, updateData)
        );
        await Promise.all(promises);
      }
      
      toast.success(`${selectedLeads.length} leads updated successfully`);
      setBulkUpdateDialogOpen(false);
      setBulkUpdateField('');
      setBulkUpdateValue('');
      setBulkUpdateValues([]);
      setSelectedIds(new Set());
      setSelectAllPages(false);
      refreshLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update leads');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Fetch activities for hover card
  const fetchLeadActivities = async (leadId) => {
    // Check cache first
    if (leadActivitiesCache[leadId]) {
      return leadActivitiesCache[leadId];
    }

    try {
      const response = await axios.get(`${API}/communications/lead/${leadId}`);
      const activities = response.data.slice(0, 5);
      
      // Cache the results
      setLeadActivitiesCache(prev => ({
        ...prev,
        [leadId]: activities
      }));
      
      return activities;
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      // Cache empty array to prevent repeated failed requests
      setLeadActivitiesCache(prev => ({
        ...prev,
        [leadId]: []
      }));
      return [];
    }
  };

  // AI Call functions
  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/ai-calls/campaigns`);
      setCampaigns(response.data.filter(c => c.status === 'active'));
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    }
  };

  const openAICallDialog = () => {
    fetchCampaigns();
    setAiCallDialogOpen(true);
  };

  const handleBulkAICall = async () => {
    if (!selectedCampaignId) {
      toast.error('Please select a campaign');
      return;
    }
    
    const leadsWithPhone = Array.from(selectedIds).filter(id => {
      const lead = leads.find(l => l.id === id);
      return lead && (lead.work_direct_phone || lead.mobile_phone || lead.work_company_phone);
    });
    
    if (leadsWithPhone.length === 0) {
      toast.error('None of the selected leads have phone numbers');
      return;
    }
    
    setAiCallLoading(true);
    try {
      const response = await axios.post(`${API}/ai-calls/bulk-call`, {
        lead_ids: leadsWithPhone,
        campaign_id: selectedCampaignId
      });
      
      const { successful, total } = response.data;
      toast.success(`AI calls initiated for ${successful}/${total} leads`);
      setAiCallDialogOpen(false);
      setSelectedIds(new Set());
      setSelectedCampaignId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate AI calls');
    } finally {
      setAiCallLoading(false);
    }
  };

  // Sequence Enrollment functions
  const fetchSequences = async () => {
    try {
      const response = await axios.get(`${API}/sequences?status=active`);
      setSequences(response.data || []);
    } catch (error) {
      console.error('Failed to load sequences:', error);
    }
  };

  const openSequenceDialog = () => {
    fetchSequences();
    setSequenceDialogOpen(true);
  };

  const handleEnrollInSequence = async () => {
    if (!selectedSequenceId) {
      toast.error('Please select a sequence');
      return;
    }
    
    setSequenceEnrollLoading(true);
    try {
      const response = await axios.post(`${API}/sequences/${selectedSequenceId}/enroll`, {
        lead_ids: Array.from(selectedIds)
      });
      
      const { enrolled, skipped, total } = response.data;
      if (enrolled > 0) {
        toast.success(`${enrolled} leads enrolled in sequence`);
      }
      if (skipped > 0) {
        toast.warning(`${skipped} leads skipped (already enrolled or ineligible)`);
      }
      
      setSequenceDialogOpen(false);
      setSelectedIds(new Set());
      setSelectedSequenceId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll leads');
    } finally {
      setSequenceEnrollLoading(false);
    }
  };

  const handleExportCSV = () => {
    const headers = [
      'First Name', 'Last Name', 'Prefix', 'Title', 'Company', 'Location',
      'Work Email', 'Personal Email', 'Work Direct Phone', 'Work Company Phone', 'Mobile Phone',
      'LinkedIn URL', 'X/Twitter URL', 'Source', 'Timezone', 'Status', 'Lead Types',
      'DNC', 'DNE', 'Bad Number', 'Envelope Mailed', 'Batch Name', 'Notes', 'Created At'
    ];
    const rows = filteredLeads.map(l => [
      l.first_name || '',
      l.last_name || '',
      l.prefix || '',
      l.title || '',
      l.company_name || '',
      l.location_name || '',
      l.work_email || '',
      l.personal_email || '',
      l.work_direct_phone || '',
      l.work_company_phone || '',
      l.mobile_phone || '',
      l.linkedin_url || '',
      l.x_url || '',
      l.source || '',
      l.timezone || '',
      l.status || '',
      (l.lead_types || []).join('; '),
      l.dnc ? 'Yes' : 'No',
      l.dne ? 'Yes' : 'No',
      l.bad_number ? 'Yes' : 'No',
      l.envelope_mailed ? 'Yes' : 'No',
      l.batch_name || '',
      l.description || '',
      l.created_at ? format(new Date(l.created_at), 'yyyy-MM-dd HH:mm') : ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Per-tab counts (always visible, not just active tab)
  const tabCounts = useMemo(() => ({
    all: leads.length,
    jobtalk: leads.filter(l => !(l.lead_types || []).includes('franchise')).length,
    franchise: leads.filter(l => (l.lead_types || []).includes('franchise')).length,
  }), [leads]);

  // Filter leads
  const filteredLeads = useMemo(() => {
    let result = leads;
    // Tab filter (client-side — data always fetched unfiltered)
    if (leadCategoryTab === 'franchise') {
      result = result.filter(l => (l.lead_types || []).includes('franchise'));
    } else if (leadCategoryTab === 'jobtalk') {
      result = result.filter(l => !(l.lead_types || []).includes('franchise'));
    }
    if (hideDNC) result = result.filter(l => !l.dnc);
    
    // Filter by selected lead types
    if (selectedLeadTypes.length > 0) {
      result = result.filter(l => {
        const leadTypes = l.lead_types || [];
        return selectedLeadTypes.some(type => leadTypes.includes(type));
      });
    }
    
    // Filter by selected lists
    if (selectedLists.length > 0) {
      result = result.filter(l => {
        const leadListIds = l.list_ids || [];
        return selectedLists.some(listId => leadListIds.includes(listId));
      });
    }

    // Filter by selected sources
    if (selectedSources.length > 0) {
      result = result.filter(l => selectedSources.includes(l.source || ''));
    }

    // Filter by score band
    if (scoreBandFilter) {
      result = result.filter(l => l.lead_score_band === scoreBandFilter);
    }

    if (searchQuery.trim()) {
      const words = searchQuery.toLowerCase().trim().split(/\s+/);
      result = result.filter(l => {
        const textFields = [
          (l.first_name || '').toLowerCase(),
          (l.last_name || '').toLowerCase(),
          (l.work_email || '').toLowerCase(),
          (l.company_name || '').toLowerCase(),
          (l.title || '').toLowerCase()
        ];
        const fullName = `${(l.first_name || '')} ${(l.last_name || '')}`.toLowerCase();
        // Every word must match at least one field or the full name
        const textMatch = words.every(word =>
          textFields.some(field => field.includes(word)) || fullName.includes(word)
        );
        if (textMatch) return true;
        // Phone number search - strip non-digits for matching
        const phoneQuery = searchQuery.replace(/\D/g, '');
        if (phoneQuery) {
          const mobileClean = (l.mobile_phone || '').replace(/\D/g, '');
          const workClean = (l.work_direct_phone || '').replace(/\D/g, '');
          if (mobileClean.includes(phoneQuery) || workClean.includes(phoneQuery)) return true;
        }
        return false;
      });
    }
    // View filter (All / New / Mine)
    if (viewFilter === 'new') {
      result = result.filter(l => l.status === 'new' && !l.owner_id);
    } else if (viewFilter === 'mine') {
      result = result.filter(l => l.owner_id === currentUser?.id);
    }
    return result;
  }, [leads, leadCategoryTab, hideDNC, searchQuery, selectedLeadTypes, selectedLists, selectedSources, scoreBandFilter, viewFilter, currentUser]);

  // Effective filtered data: grid column filters take precedence when active
  const effectiveFilteredLeads = gridFilteredData || filteredLeads;
  const isFiltered = effectiveFilteredLeads.length !== leads.length;

  // Leads with email (for email list creation)
  const filteredLeadsWithEmail = useMemo(() =>
    effectiveFilteredLeads.filter(l => l.work_email || l.personal_email),
    [effectiveFilteredLeads]
  );

  // Store filtered lead IDs for prev/next navigation on detail page
  useEffect(() => {
    if (filteredLeads.length > 0) {
      sessionStorage.setItem('jtcrm_lead_nav_ids', JSON.stringify(filteredLeads.map(l => l.id)));
    }
  }, [filteredLeads]);

  // Stats
  const stats = useMemo(() => {
    const byStatus = {};
    STATUSES.forEach(s => byStatus[s.value] = 0);
    leads.forEach(l => { if (byStatus[l.status] !== undefined) byStatus[l.status]++; });
    return byStatus;
  }, [leads]);

  // Dynamically build source list from all loaded leads (auto-includes new sources)
  const availableSources = useMemo(() => {
    const set = new Set();
    leads.forEach(l => { if (l.source) set.add(l.source); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [leads]);

  // Dynamically build owner list from leads that have an assigned owner
  const availableOwners = useMemo(() => {
    const map = new Map();
    leads.forEach(l => { if (l.owner_id && l.owner_name) map.set(l.owner_id, l.owner_name); });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
  }, [leads]);

  // Grid columns
  const columns = useMemo(() => [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      filterable: true,
      render: (row) => (
        <div className="flex items-center gap-1 group">
          <Link
            to={`/admin/leads/${row.id}`}
            className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
            data-testid={`lead-link-${row.id}`}
          >
            {row.first_name} {row.last_name}
            {row.dnc && <Badge variant="outline" className="ml-2 text-red-600 text-xs">DNC</Badge>}
          </Link>
          <a
            href={`/admin/leads/${row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Open in new tab"
            className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-indigo-600 transition-opacity"
            onClick={e => e.stopPropagation()}
          >
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      ),
      getValue: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim().toLowerCase(),
    },
    {
      key: 'company_name',
      label: 'Company',
      sortable: true,
      filterable: true,
      filterType: 'status',
      filterOptions: companies.map(c => ({ value: c.name, label: c.name })),
      render: (row) => row.company_name || '—',
    },
    {
      key: 'lead_score',
      label: 'Score',
      sortable: true,
      render: (row) => <LeadScoreBadge score={row.lead_score} band={row.lead_score_band} />,
      getValue: (row) => row.lead_score || 0,
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      filterable: true,
      render: (row) => row._contact_masked
        ? <span className="flex items-center gap-1 text-slate-400 text-xs"><Lock className="h-3 w-3" /> Owner only</span>
        : (row.title || '—'),
    },
    {
      key: 'work_email',
      label: 'Email',
      sortable: true,
      filterable: true,
      defaultWidth: 260,
      getValue: (row) => [row.work_email, row.personal_email].filter(Boolean).join(' ').toLowerCase(),
      render: (row) => {
        if (row._contact_masked) return (
          <span className="flex items-center gap-1 text-slate-400 text-xs"><Lock className="h-3 w-3" /> Owner only</span>
        );
        const emails = [
          { label: 'Work',     value: row.work_email },
          { label: 'Personal', value: row.personal_email },
        ].filter(e => e.value);
        if (emails.length === 0) return '—';
        return (
          <div className="flex flex-col gap-1">
            {emails.map(({ label, value }) => (
              <button
                key={value}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/leads/${row.id}`, { state: { openEmail: true } });
                }}
                title={`Email ${label}: ${value}`}
                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline text-xs text-left group"
              >
                <Mail className="h-3 w-3 flex-shrink-0" />
                <span className="text-slate-400 w-12 flex-shrink-0">{label}</span>
                <span className="whitespace-nowrap">{value}</span>
              </button>
            ))}
          </div>
        );
      },
    },
    {
      key: 'phone',
      label: 'Phone',
      sortable: false,
      filterable: true,
      defaultWidth: 260,
      getValue: (row) => {
        return [row.mobile_phone, row.work_direct_phone, row.work_company_phone]
          .filter(Boolean).join(' ').toLowerCase();
      },
      render: (row) => {
        if (row._contact_masked) return (
          <span className="flex items-center gap-1 text-slate-400 text-xs"><Lock className="h-3 w-3" /> Owner only</span>
        );
        const numbers = [
          { label: 'Mobile',  number: row.mobile_phone,        icon: '📱' },
          { label: 'Direct',  number: row.work_direct_phone,   icon: '📞' },
          { label: 'Company', number: row.work_company_phone,  icon: '🏢' },
        ].filter(n => n.number);

        if (numbers.length === 0) return '—';

        return (
          <div className="flex flex-col gap-1">
            {numbers.map(({ label, number, icon }) => (
              <button
                key={number}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/leads/${row.id}`, { state: { openCall: true, phoneNumber: number } });
                }}
                title={`Call ${label}: ${number}`}
                className="flex items-center gap-1 text-blue-600 hover:text-green-600 hover:underline text-xs text-left group"
              >
                <Phone className="h-3 w-3 flex-shrink-0 group-hover:text-green-600" />
                <span className="text-slate-400 w-12 flex-shrink-0">{label}</span>
                <span className="whitespace-nowrap">{formatPhone(number)}</span>
              </button>
            ))}
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterType: 'status',
      filterOptions: allStatuses.map(s => ({ value: s.value, label: s.label })),
      render: (row) => {
        const s = allStatuses.find(st => st.value === row.status);
        if (s) return <Badge className={s.color}>{s.label}</Badge>;
        return <Badge className="bg-slate-100 text-slate-700">{row.status || 'New'}</Badge>;
      },
    },
    {
      key: 'owner_name',
      label: 'Owner',
      sortable: true,
      filterable: true,
      filterType: 'status',
      filterOptions: availableOwners.map(name => ({ value: name, label: name })),
      render: (row) => (
        <OwnerBadge
          ownerId={row.owner_id}
          ownerName={row.owner_name}
          status={row.status}
          currentUserId={currentUser?.id}
          onClaim={() => claimLead(row.id)}
          claiming={claimingId === row.id}
        />
      ),
    },
    {
      key: 'source',
      label: 'Source',
      sortable: true,
      filterable: true,
      filterType: 'status',
      filterOptions: availableSources.map(s => ({ value: s, label: s })),
      render: (row) => row.source || '—',
    },
    {
      key: 'envelope_mailed',
      label: 'Envelope Mailed',
      sortable: true,
      filterable: true,
      filterType: 'boolean',
      filterOptions: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      render: (row) => (
        <Badge variant="outline" className={row.envelope_mailed ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50 text-slate-600 border-slate-200'}>
          {row.envelope_mailed ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    {
      key: 'last_activity_at',
      label: 'Last Activity',
      sortable: true,
      sortType: 'date',
      render: (row) => {
        if (!row.last_activity_at) return <span className="text-slate-300 text-xs">No activity</span>;
        try {
          return (
            <span className="text-slate-600 text-xs" title={format(parseISO(row.last_activity_at), 'MMM d, yyyy h:mm a')}>
              {formatDistanceToNow(parseISO(row.last_activity_at), { addSuffix: true })}
            </span>
          );
        } catch { return '—'; }
      },
      getValue: (row) => row.last_activity_at || '',
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (row) => row.created_at ? format(parseISO(row.created_at), 'MMM d, yyyy') : '—',
      getValue: (row) => row.created_at || '',
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      render: (row) => row.can_delete !== false ? (
        <Button
          variant="ghost"
          size="sm"
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={(e) => { e.stopPropagation(); setSelectedLead(row); setDeleteDialogOpen(true); }}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ) : null,
    },
  ], [navigate, companies, users, claimLead, claimingId, currentUser?.id, availableSources, availableOwners]);

  return (
    <div className="space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">CRM Leads</h1>
          <p className={isFiltered ? "text-indigo-600 font-medium" : "text-slate-500"}>
            {isFiltered ? `${effectiveFilteredLeads.length} of ${leads.length} leads (filtered)` : `${leads.length} total leads`}
          </p>
          <div className="flex items-center gap-1.5 mt-1">
            {[
              { band: "hot", color: "text-green-600 bg-green-50 border-green-200" },
              { band: "warm", color: "text-amber-600 bg-amber-50 border-amber-200" },
              { band: "cold", color: "text-blue-600 bg-blue-50 border-blue-200" },
              { band: "inactive", color: "text-gray-500 bg-gray-50 border-gray-200" },
            ].map(({ band, color }) => (
              <button
                key={band}
                onClick={() => setScoreBandFilter(scoreBandFilter === band ? "" : band)}
                className={`px-2 py-0.5 rounded border text-xs font-medium capitalize transition-all ${
                  scoreBandFilter === band ? "bg-indigo-600 text-white border-indigo-600" : color
                }`}
              >
                {scoreBands[band] || 0} {band}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant={showFilters ? "default" : "outline"} 
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-4 w-4 mr-2" />
            {showFilters ? "Hide Filters" : "Filter"}
          </Button>
          <Button variant="outline" onClick={() => setCsvImportOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />Import
          </Button>
          <Button variant="outline" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />Export
          </Button>
          {isFiltered && (
            <Button variant="outline" className="border-indigo-300 text-indigo-600 hover:bg-indigo-50" onClick={() => { setEmailListName(''); setEmailListDescription(''); setEmailListDialogOpen(true); }}>
              <ListPlus className="h-4 w-4 mr-2" />Create Email List
            </Button>
          )}
          <Button onClick={() => { resetForm(); setFormData(f => ({ ...f, owner_id: currentUser?.id || '', owner_name: currentUser?.full_name || '' })); setCreateDialogOpen(true); }} data-testid="create-lead-btn">
            <Plus className="h-4 w-4 mr-2" />Add Lead
          </Button>
        </div>
      </div>

      {/* View filter toggle */}
      <div className="flex items-center gap-1 mb-2">
        {[
          { value: 'all', label: 'All Leads', count: leads.length },
          { value: 'new', label: 'New', count: leads.filter(l => l.status === 'new' && !l.owner_id).length },
          { value: 'mine', label: 'My Leads', count: leads.filter(l => l.owner_id === currentUser?.id).length },
        ].map(tab => (
          <button
            key={tab.value}
            onClick={() => {
              setViewFilter(tab.value);
              try { const s = JSON.parse(localStorage.getItem('jtcrm_leads_filters') || '{}'); localStorage.setItem('jtcrm_leads_filters', JSON.stringify({ ...s, viewFilter: tab.value })); } catch {}
            }}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              viewFilter === tab.value
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {tab.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              viewFilter === tab.value ? 'bg-indigo-500 text-white' : 'bg-slate-200 text-slate-500'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Lead Category Tabs with Lead Type Toggles */}
      <Tabs value={leadCategoryTab} onValueChange={handleLeadCategoryChange}>
        <div className="flex items-center justify-between gap-4">
          <TabsList className="grid w-full max-w-2xl grid-cols-3">
            <TabsTrigger value="all" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              All
              <Badge variant="secondary">{tabCounts.all}</Badge>
            </TabsTrigger>
            <TabsTrigger value="jobtalk" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              JobTalk
              <Badge variant="secondary">{tabCounts.jobtalk}</Badge>
            </TabsTrigger>
            <TabsTrigger value="franchise" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Franchise
              <Badge variant="secondary">{tabCounts.franchise}</Badge>
            </TabsTrigger>
          </TabsList>

          {/* Lead Type Toggle Buttons - Right Aligned */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {LEAD_TYPES.map(type => (
              <Button
                key={type.value}
                variant={selectedLeadTypes.includes(type.value) ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  if (selectedLeadTypes.includes(type.value)) {
                    setSelectedLeadTypes(selectedLeadTypes.filter(t => t !== type.value));
                  } else {
                    setSelectedLeadTypes([...selectedLeadTypes, type.value]);
                  }
                }}
                className={`text-xs h-8 px-2 ${selectedLeadTypes.includes(type.value) ? type.color.replace('-100', '-600').replace('-700', '-50') : ''}`}
                data-testid={`toggle-${type.value}`}
              >
                {type.label}
              </Button>
            ))}
          </div>
        </div>

        <TabsContent value={leadCategoryTab} className="space-y-4 mt-4">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      ) : (<>
      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <Card><CardContent className="p-4 text-center">
          <div className="text-2xl font-bold text-slate-900">{total}</div>
          <div className="text-sm text-slate-500">Total Leads</div>
        </CardContent></Card>
        {STATUSES.slice(0, 4).map(s => (
          <Card key={s.value}><CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-slate-900">{stats[s.value] || 0}</div>
            <div className="text-sm text-slate-500">{s.label}</div>
          </CardContent></Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-2xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name, email, company, phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10"
            data-testid="search-leads"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="h-4 w-4 text-slate-400 hover:text-slate-600" />
            </button>
          )}
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={handleReset}
          title="Reset filters (use cached data)"
          className="shrink-0"
          data-testid="reset-leads-btn"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh data from database"
          className="shrink-0"
          data-testid="refresh-leads-btn"
        >
          {refreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
        <div className="flex items-center gap-2">
          <Checkbox id="hideDNC" checked={hideDNC} onCheckedChange={setHideDNC} />
          <Label htmlFor="hideDNC" className="text-sm cursor-pointer">Hide DNC</Label>
        </div>
        
        {/* Lead Types Multi-Select Filter */}
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLeadTypeDropdownOpen(!leadTypeDropdownOpen)}
            className="gap-2"
            data-testid="lead-type-filter"
          >
            Lead Types
            {selectedLeadTypes.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">{selectedLeadTypes.length}</Badge>
            )}
          </Button>
          {leadTypeDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-56 bg-white border rounded-lg shadow-lg z-50 p-2">
              <div className="flex justify-between items-center mb-2 pb-2 border-b">
                <span className="text-xs font-medium text-slate-500">Filter by Type</span>
                <div className="flex items-center gap-2">
                  {selectedLeadTypes.length > 0 && (
                    <button
                      onClick={() => setSelectedLeadTypes([])}
                      className="text-xs text-indigo-600 hover:underline"
                    >
                      Clear
                    </button>
                  )}
                  <button
                    onClick={() => setLeadTypeDropdownOpen(false)}
                    className="text-slate-400 hover:text-slate-600"
                    data-testid="close-lead-type-dropdown"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {LEAD_TYPES.map(type => (
                <label
                  key={type.value}
                  className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedLeadTypes.includes(type.value)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedLeadTypes([...selectedLeadTypes, type.value]);
                      } else {
                        setSelectedLeadTypes(selectedLeadTypes.filter(t => t !== type.value));
                      }
                    }}
                  />
                  <Badge className={`${type.color} text-xs`}>{type.label}</Badge>
                </label>
              ))}
            </div>
          )}
        </div>
        
        {/* Lists Multi-Select Filter */}
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setListsDropdownOpen(!listsDropdownOpen)}
            className="gap-2"
            data-testid="lists-filter"
          >
            Lists
            {selectedLists.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">{selectedLists.length}</Badge>
            )}
          </Button>
          {listsDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-white border rounded-lg shadow-lg z-50 p-2 max-h-96 overflow-y-auto">
              <div className="flex justify-between items-center mb-2 pb-2 border-b">
                <span className="text-xs font-medium text-slate-500">Filter by Lists</span>
                <div className="flex items-center gap-2">
                  {selectedLists.length > 0 && (
                    <button
                      onClick={() => setSelectedLists([])}
                      className="text-xs text-indigo-600 hover:underline"
                      title="Clear list filters"
                    >
                      Clear
                    </button>
                  )}
                  <button
                    onClick={() => setListsDropdownOpen(false)}
                    className="text-slate-400 hover:text-slate-600"
                    data-testid="close-lists-dropdown"
                    title="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              {/* ALL option at the top */}
              <label
                className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer mb-1 border-b"
              >
                <Checkbox
                  checked={selectedLists.length === 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedLists([]);
                    }
                  }}
                />
                <div className="flex items-center gap-2 flex-1">
                  <div className="w-3 h-3 rounded-full flex-shrink-0 bg-slate-400" />
                  <span className="text-sm font-semibold text-slate-900">ALL</span>
                </div>
              </label>
              
              {lists.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-2">No lists available</p>
              ) : (
                <>
                  {lists.map(list => (
                    <label
                      key={list.id}
                      className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedLists.includes(list.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedLists([...selectedLists, list.id]);
                          } else {
                            setSelectedLists(selectedLists.filter(id => id !== list.id));
                          }
                        }}
                      />
                      <div className="flex items-center gap-2 flex-1">
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: list.color }}
                        />
                        <span className="text-sm text-slate-700">{list.name}</span>
                      </div>
                    </label>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* Source Multi-Select Filter */}
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSourceDropdownOpen(!sourceDropdownOpen)}
            className="gap-2"
            data-testid="source-filter"
          >
            Source
            {selectedSources.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">{selectedSources.length}</Badge>
            )}
          </Button>
          {sourceDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-56 bg-white border rounded-lg shadow-lg z-50 p-2 max-h-80 overflow-y-auto">
              <div className="flex justify-between items-center mb-2 pb-2 border-b">
                <span className="text-xs font-medium text-slate-500">Filter by Source</span>
                <div className="flex items-center gap-2">
                  {selectedSources.length > 0 && (
                    <button onClick={() => setSelectedSources([])} className="text-xs text-indigo-600 hover:underline">Clear</button>
                  )}
                  <button onClick={() => setSourceDropdownOpen(false)} className="text-slate-400 hover:text-slate-600">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {availableSources.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-2">No sources found</p>
              ) : (
                availableSources.map(source => (
                  <label key={source} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer">
                    <Checkbox
                      checked={selectedSources.includes(source)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedSources([...selectedSources, source]);
                        } else {
                          setSelectedSources(selectedSources.filter(s => s !== source));
                        }
                      }}
                    />
                    <span className="text-sm text-slate-700">{source}</span>
                  </label>
                ))
              )}
            </div>
          )}
        </div>

        {selectedIds.size > 0 && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBulkUpdateDialogOpen(true)}
              data-testid="bulk-update-btn"
            >
              <Edit className="h-4 w-4 mr-2" />Bulk Update
            </Button>
            <Button 
              size="sm" 
              onClick={openAICallDialog}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="bulk-ai-call-btn"
            >
              <Bot className="h-4 w-4 mr-2" />AI Call {selectedIds.size} Selected
            </Button>
            <Button 
              size="sm" 
              onClick={openSequenceDialog}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="bulk-sequence-btn"
            >
              <Zap className="h-4 w-4 mr-2" />Enroll in Sequence
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setBulkDeleteDialogOpen(true)}>
              <Trash2 className="h-4 w-4 mr-2" />Delete {selectedIds.size} Selected
            </Button>
          </div>
        )}
      </div>

      {/* Select All Pages Banner */}
      {selectedIds.size > 0 && selectedIds.size >= 25 && selectedIds.size < effectiveFilteredLeads.length && !selectAllPages && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                All {selectedIds.size} leads on this page are selected.
              </p>
              <p className="text-xs text-blue-700">
                Select all {effectiveFilteredLeads.length} {isFiltered ? 'filtered ' : ''}leads across all pages?
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectAllPages(true)}
            className="border-blue-300 text-blue-700 hover:bg-blue-100"
          >
            Select All {effectiveFilteredLeads.length} Leads
          </Button>
        </div>
      )}

      {/* Select All Pages Active Banner */}
      {selectAllPages && (
        <div className="bg-indigo-50 border border-indigo-300 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-indigo-600" />
            <p className="text-sm font-semibold text-indigo-900">
              All {effectiveFilteredLeads.length} {isFiltered ? 'filtered ' : ''}leads across all pages are selected.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSelectAllPages(false);
              setSelectedIds(new Set());
            }}
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
          >
            Clear Selection
          </Button>
        </div>
      )}

      {/* Data Grid */}
      {prefsLoaded && (
        <AdvancedDataGrid
          data={filteredLeads}
          columns={columns}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          savedPreferences={columnPrefs}
          onColumnChange={handleColumnChange}
          emptyMessage="No leads found"
          showFilters={showFilters}
          defaultSort={{ key: 'last_activity_at', direction: 'desc' }}
          storageKey="jtcrm_leads_col_filters"
          onFilteredDataChange={handleGridFilteredDataChange}
          onHoverOpen={(row, columnKey, open) => {
            // Fetch activities when hover card opens
            if (open && columnKey === 'name' && !leadActivitiesCache[row.id]) {
              fetchLeadActivities(row.id);
            }
          }}
          renderRowHoverContent={(row, columnKey) => {
            if (columnKey === 'name') {
              const activities = leadActivitiesCache[row.id] || null;

              return (
                <div className="p-4 w-80">
                  <h4 className="font-semibold text-sm mb-3 text-slate-900">
                    Recent Activity - {row.first_name} {row.last_name}
                  </h4>
                  {!activities ? (
                    <div className="flex items-center gap-2 text-slate-500 text-xs">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span>Loading activities...</span>
                    </div>
                  ) : activities.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">No recent activity</p>
                  ) : (
                    <div className="space-y-2">
                      {activities.map((activity, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs border-l-2 border-blue-200 pl-2 py-1">
                          <div className="flex-shrink-0 mt-0.5">
                            {activity.type === 'email' && <Mail className="h-3 w-3 text-blue-600" />}
                            {activity.type === 'sms' && <MessageSquare className="h-3 w-3 text-green-600" />}
                            {activity.type === 'call' && <Phone className="h-3 w-3 text-purple-600" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-700 truncate">
                              {activity.type === 'email' && `Email: ${activity.subject || 'No subject'}`}
                              {activity.type === 'sms' && `SMS: ${(activity.message || 'Message sent').substring(0, 30)}...`}
                              {activity.type === 'call' && `Call: ${activity.outcome || 'Call logged'}`}
                            </p>
                            <p className="text-slate-500">
                              {activity.created_at ? format(parseISO(activity.created_at), 'MMM d, h:mm a') : 'No date'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return null;
          }}
        />
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Lead</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label>Prefix</Label>
              <Select value={formData.prefix || 'none'} onValueChange={(v) => setFormData({...formData, prefix: v === 'none' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {PREFIXES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>First Name *</Label>
              <Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} data-testid="first-name-input" />
            </div>
            <div>
              <Label>Last Name</Label>
              <Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} />
            </div>
            <div>
              <Label>Title</Label>
              <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} />
            </div>
            <div className="relative company-autocomplete">
              <Label>Company</Label>
              <div className="relative">
                <Input
                  placeholder="Type to search companies..."
                  value={companySearchQuery}
                  onChange={(e) => {
                    const query = e.target.value;
                    setCompanySearchQuery(query);
                    setShowCompanySuggestions(true);
                    // Clear selection if user is typing
                    if (query !== (companies.find(c => c.id === formData.company_id)?.name || '')) {
                      setFormData({...formData, company_id: '', location_id: ''});
                      setSelectedCompanyLocations([]);
                    }
                  }}
                  onFocus={() => setShowCompanySuggestions(true)}
                  className="bg-white text-slate-900"
                  style={{ color: '#000000' }}
                  autoComplete="off"
                />
                
                {showCompanySuggestions && companySearchQuery && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {companies
                      .filter(c => c.name.toLowerCase().includes(companySearchQuery.toLowerCase()))
                      .slice(0, 10)
                      .map((company) => (
                        <div
                          key={company.id}
                          className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm text-slate-900"
                          onClick={() => {
                            setFormData({...formData, company_id: company.id, location_id: ''});
                            setCompanySearchQuery(company.name);
                            setShowCompanySuggestions(false);
                            handleCompanyChange(company.id);
                          }}
                        >
                          {company.name}
                        </div>
                      ))}
                    {companies.filter(c => c.name.toLowerCase().includes(companySearchQuery.toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-sm text-slate-400 italic">No companies found</div>
                    )}
                    <div className="border-t px-3 py-2">
                      <button
                        type="button"
                        className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium w-full"
                        onClick={() => {
                          setNewCompanyForm({ name: companySearchQuery.trim(), website: '', num_recruiters: '', num_reqs_open: '', geographies: '', years_in_business: '', revenues: '', ats_used: '', ai_voice_using: '', other_ai_tools: '', company_notes: '' });
                          setShowCompanySuggestions(false);
                          setNewCompanyDialogOpen(true);
                        }}
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add New Company
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {selectedCompanyLocations.length > 0 && (
              <div>
                <Label>Location</Label>
                <Select value={formData.location_id || 'none'} onValueChange={(v) => setFormData({...formData, location_id: v === 'none' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {selectedCompanyLocations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Work Email</Label>
              <Input type="email" value={formData.work_email} onChange={(e) => setFormData({...formData, work_email: e.target.value})} />
            </div>
            <div>
              <Label>Mobile Phone</Label>
              <Input type="tel" value={formData.mobile_phone} onChange={(e) => setFormData({...formData, mobile_phone: e.target.value})} />
            </div>
            <div>
              <Label>Work Direct Phone</Label>
              <Input type="tel" value={formData.work_direct_phone} onChange={(e) => setFormData({...formData, work_direct_phone: e.target.value})} />
            </div>
            <div>
              <Label>Work Company Phone</Label>
              <Input type="tel" value={formData.work_company_phone} onChange={(e) => setFormData({...formData, work_company_phone: e.target.value})} />
            </div>
            <div>
              <Label>Personal Email</Label>
              <Input type="email" value={formData.personal_email} onChange={(e) => setFormData({...formData, personal_email: e.target.value})} />
            </div>
            <div>
              <Label>LinkedIn URL</Label>
              <Input value={formData.linkedin_url} onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})} />
            </div>
            <div>
              <Label>Source</Label>
              <SourceSelect
                value={formData.source}
                onValueChange={(v) => setFormData({...formData, source: v})}
                existingSources={availableSources}
              />
            </div>
            <div>
              <Label>Status</Label>
              <StatusSelect
                value={formData.status}
                onValueChange={(v) => setFormData({...formData, status: v})}
                allStatuses={allStatuses}
                addStatus={addStatus}
              />
            </div>
            <div>
              <Label>Owner</Label>
              <Popover open={ownerSearchOpen} onOpenChange={(open) => { setOwnerSearchOpen(open); if (!open) setOwnerSearch(''); }}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between font-normal">
                    {formData.owner_name
                      ? <span>{formData.owner_name}</span>
                      : <span className="text-slate-400">Assign owner...</span>
                    }
                    <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput placeholder="Search users..." value={ownerSearch} onValueChange={setOwnerSearch} />
                    <CommandList className="max-h-52 overflow-y-auto">
                      <CommandGroup>
                        <CommandItem onSelect={() => { setFormData({...formData, owner_id: '', owner_name: ''}); setOwnerSearch(''); setOwnerSearchOpen(false); }}>
                          <Check className={`w-4 h-4 mr-2 ${!formData.owner_id ? 'opacity-100' : 'opacity-0'}`} />
                          <span className="text-slate-400">No Owner</span>
                        </CommandItem>
                        {users
                          .filter(u => !ownerSearch.trim() || (u.full_name || u.email || '').toLowerCase().includes(ownerSearch.toLowerCase()))
                          .map(u => (
                            <CommandItem key={u.id} onSelect={() => { setFormData({...formData, owner_id: u.id, owner_name: u.full_name || u.email}); setOwnerSearch(''); setOwnerSearchOpen(false); }}>
                              <Check className={`w-4 h-4 mr-2 ${formData.owner_id === u.id ? 'opacity-100' : 'opacity-0'}`} />
                              {u.full_name || u.email}
                            </CommandItem>
                          ))
                        }
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} />
            </div>
            <div className="col-span-2 flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Checkbox checked={formData.dnc} onCheckedChange={(c) => setFormData({...formData, dnc: c})} />
                <Label>Do Not Call</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={formData.dne} onCheckedChange={(c) => setFormData({...formData, dne: c})} />
                <Label>Do Not Email</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Company Dialog (opened from company autocomplete) */}
      <Dialog open={newCompanyDialogOpen} onOpenChange={setNewCompanyDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add New Company</DialogTitle></DialogHeader>
          <div className="space-y-6 py-4">
            <p className="text-xs text-slate-500 -mt-2">Fields marked with <span className="text-red-500 font-semibold">*</span> are required. All other fields are optional.</p>
            <div className="space-y-2">
              <Label>Company Name <span className="text-red-500">*</span></Label>
              <Input value={newCompanyForm.name} onChange={(e) => setNewCompanyForm({...newCompanyForm, name: e.target.value})} placeholder="e.g., Acme Corporation" />
            </div>
            <div className="space-y-2">
              <Label>Website</Label>
              <Input value={newCompanyForm.website} onChange={(e) => setNewCompanyForm({...newCompanyForm, website: e.target.value})} placeholder="e.g., https://acme.com" />
            </div>

            {/* Tech Stack */}
            <div className="border-t pt-4">
              <h3 className="font-medium text-slate-900 mb-4">Tech Stack</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>ATS Used</Label>
                  <Input list="lead-ats-options" value={newCompanyForm.ats_used} onChange={(e) => setNewCompanyForm({...newCompanyForm, ats_used: e.target.value})} placeholder="e.g., JobDiva" />
                  <datalist id="lead-ats-options">{ATS_OPTIONS.map(opt => <option key={opt} value={opt} />)}</datalist>
                </div>
                <div className="space-y-2">
                  <Label>AI Voice Using</Label>
                  <Input list="lead-ai-voice-options" value={newCompanyForm.ai_voice_using} onChange={(e) => setNewCompanyForm({...newCompanyForm, ai_voice_using: e.target.value})} placeholder="e.g., JobTalk" />
                  <datalist id="lead-ai-voice-options">{AI_VOICE_OPTIONS.map(opt => <option key={opt} value={opt} />)}</datalist>
                </div>
                <div className="space-y-2">
                  <Label>Other AI Tools</Label>
                  <Input value={newCompanyForm.other_ai_tools} onChange={(e) => setNewCompanyForm({...newCompanyForm, other_ai_tools: e.target.value})} placeholder="e.g., ChatGPT, Copilot" />
                </div>
              </div>
            </div>

            {/* Company Details */}
            <div className="border-t pt-4">
              <h3 className="font-medium text-slate-900 mb-4">Company Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label># of Recruiters</Label>
                  <Input type="number" min="0" value={newCompanyForm.num_recruiters} onChange={(e) => setNewCompanyForm({...newCompanyForm, num_recruiters: e.target.value})} placeholder="e.g., 25" />
                </div>
                <div className="space-y-2">
                  <Label>Number of Reqs Open</Label>
                  <Input type="number" min="0" value={newCompanyForm.num_reqs_open} onChange={(e) => setNewCompanyForm({...newCompanyForm, num_reqs_open: e.target.value})} placeholder="e.g., 100" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Geographies</Label>
                  <Input value={newCompanyForm.geographies} onChange={(e) => setNewCompanyForm({...newCompanyForm, geographies: e.target.value})} placeholder="e.g., USA, Canada, UK" />
                </div>
                <div className="space-y-2">
                  <Label>Years in Business</Label>
                  <Input type="number" min="0" value={newCompanyForm.years_in_business} onChange={(e) => setNewCompanyForm({...newCompanyForm, years_in_business: e.target.value})} placeholder="e.g., 15" />
                </div>
                <div className="space-y-2">
                  <Label>Revenues</Label>
                  <Input value={newCompanyForm.revenues} onChange={(e) => setNewCompanyForm({...newCompanyForm, revenues: e.target.value})} placeholder="e.g., $50M" />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="border-t pt-4">
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={newCompanyForm.company_notes}
                  onChange={(e) => setNewCompanyForm({...newCompanyForm, company_notes: e.target.value})}
                  placeholder="Add notes about this company..."
                  className="min-h-[80px] resize-none text-sm"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCompanyDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCompany} disabled={savingCompany}>
              {savingCompany && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedLead?.first_name} {selectedLead?.last_name}? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} Leads?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700">Delete All</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CSV Import Wizard */}
      <CSVImportWizard
        open={csvImportOpen}
        onOpenChange={setCsvImportOpen}
        onImportComplete={refreshLeads}
      />

      {/* Bulk AI Call Dialog */}
      <Dialog open={aiCallDialogOpen} onOpenChange={setAiCallDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-purple-600" />
              Initiate AI Calls
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              You&apos;re about to initiate AI calls to <strong>{selectedIds.size}</strong> selected leads.
            </p>
            
            {campaigns.length > 0 ? (
              <div className="space-y-2">
                <Label>Select Campaign *</Label>
                <Select value={selectedCampaignId} onValueChange={setSelectedCampaignId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an active campaign" />
                  </SelectTrigger>
                  <SelectContent>
                    {campaigns.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <strong>No active campaigns</strong>
                <p className="mt-1">Create and activate a campaign in AI Calls before making bulk calls.</p>
              </div>
            )}
            
            <div className="p-3 bg-slate-50 rounded-lg text-xs text-slate-600">
              <p><strong>Note:</strong> Only leads with phone numbers will be called. Leads marked as DNC (Do Not Call) will be skipped.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiCallDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkAICall} 
              disabled={!selectedCampaignId || aiCallLoading || campaigns.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {aiCallLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Initiating...
                </>
              ) : (
                <>
                  <Phone className="h-4 w-4 mr-2" />
                  Start AI Calls
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Bulk Update Dialog */}
      <Dialog open={bulkUpdateDialogOpen} onOpenChange={setBulkUpdateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Bulk Update {selectAllPages ? `All ${total}` : selectedIds.size} Leads
            </DialogTitle>
            <p className="text-sm text-slate-500">
              {selectAllPages 
                ? `Update all ${total} leads across all pages. Changes will be applied to all leads matching current filters.`
                : `Update ${selectedIds.size} selected leads. Changes will be applied immediately.`
              }
            </p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Field to Update *</Label>
              <Select value={bulkUpdateField} onValueChange={(value) => {
                setBulkUpdateField(value);
                setBulkUpdateValue('');
                setBulkUpdateValues([]);
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select field..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="status">Status</SelectItem>
                  <SelectItem value="lead_types">Lead Types (Replace All)</SelectItem>
                  <SelectItem value="add_to_lists">Add to Lists</SelectItem>
                  <SelectItem value="owner_id">Owner</SelectItem>
                  <SelectItem value="source">Source</SelectItem>
                  <SelectItem value="title">Title/Position</SelectItem>
                  <SelectItem value="description">Notes/Description</SelectItem>
                  <SelectItem value="dnc">Do Not Call</SelectItem>
                  <SelectItem value="dne">Do Not Email</SelectItem>
                  <SelectItem value="envelope_mailed">Envelope Mailed</SelectItem>
                  <SelectItem value="bad_number">Bad Number</SelectItem>
                  <SelectItem value="gender">Gender</SelectItem>
                  <SelectItem value="prefix">Prefix (Mr/Ms/Dr)</SelectItem>
                  <SelectItem value="timezone">Timezone</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status Field */}
            {bulkUpdateField === 'status' && (
              <div className="space-y-2">
                <Label>Select Status *</Label>
                <StatusSelect
                  value={bulkUpdateValue}
                  onValueChange={setBulkUpdateValue}
                  allStatuses={allStatuses}
                  addStatus={addStatus}
                />
              </div>
            )}

            {/* Lead Types Field */}
            {bulkUpdateField === 'lead_types' && (
              <div className="space-y-2">
                <Label>Select Lead Types *</Label>
                <div className="border rounded-lg p-3 space-y-2 max-h-64 overflow-y-auto">
                  {LEAD_TYPES.map(type => (
                    <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={bulkUpdateValues.includes(type.value)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setBulkUpdateValues([...bulkUpdateValues, type.value]);
                          } else {
                            setBulkUpdateValues(bulkUpdateValues.filter(v => v !== type.value));
                          }
                        }}
                      />
                      <Badge className={`${type.color} text-xs`}>{type.label}</Badge>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-amber-600">This will REPLACE existing lead types</p>
              </div>
            )}

            {/* Add to Lists Field */}
            {bulkUpdateField === 'add_to_lists' && (
              <div className="space-y-2">
                <Label>Select Lists *</Label>
                <div className="border rounded-lg p-3 space-y-2 max-h-64 overflow-y-auto">
                  {lists.map(list => (
                    <label key={list.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={bulkUpdateValues.includes(list.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setBulkUpdateValues([...bulkUpdateValues, list.id]);
                          } else {
                            setBulkUpdateValues(bulkUpdateValues.filter(v => v !== list.id));
                          }
                        }}
                      />
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: list.color }} />
                        <span className="text-sm">{list.name}</span>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-blue-600">Leads will be added to selected lists</p>
              </div>
            )}

            {/* Owner Field */}
            {bulkUpdateField === 'owner_id' && (
              <div className="space-y-2">
                <Label>Select Owner *</Label>
                <Select value={bulkUpdateValue} onValueChange={setBulkUpdateValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose owner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Boolean Fields - DNC, DNE, Envelope Mailed, Bad Number */}
            {['dnc', 'dne', 'envelope_mailed', 'bad_number'].includes(bulkUpdateField) && (
              <div className="space-y-2">
                <Label>Set {bulkUpdateField.replace('_', ' ').toUpperCase()} to *</Label>
                <Select value={bulkUpdateValue} onValueChange={setBulkUpdateValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose value..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes / True</SelectItem>
                    <SelectItem value="false">No / False</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Gender Field */}
            {bulkUpdateField === 'gender' && (
              <div className="space-y-2">
                <Label>Select Gender *</Label>
                <Select value={bulkUpdateValue} onValueChange={setBulkUpdateValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose gender..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Prefix Field */}
            {bulkUpdateField === 'prefix' && (
              <div className="space-y-2">
                <Label>Select Prefix *</Label>
                <Select value={bulkUpdateValue} onValueChange={setBulkUpdateValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose prefix..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PREFIXES.map(prefix => (
                      <SelectItem key={prefix} value={prefix}>
                        {prefix}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Text Fields - Source, Title, Description, Timezone */}
            {['source', 'title', 'description', 'timezone'].includes(bulkUpdateField) && (
              <div className="space-y-2">
                <Label>{bulkUpdateField.charAt(0).toUpperCase() + bulkUpdateField.slice(1)} *</Label>
                {bulkUpdateField === 'description' ? (
                  <Textarea
                    placeholder={`Enter ${bulkUpdateField}...`}
                    value={bulkUpdateValue}
                    onChange={(e) => setBulkUpdateValue(e.target.value)}
                    rows={4}
                  />
                ) : (
                  <Input
                    placeholder={`Enter ${bulkUpdateField}...`}
                    value={bulkUpdateValue}
                    onChange={(e) => setBulkUpdateValue(e.target.value)}
                  />
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkUpdateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkUpdate} 
              disabled={bulkUpdating || (!bulkUpdateValue && bulkUpdateValues.length === 0)}
            >
              {bulkUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                `Update ${selectAllPages ? total : selectedIds.size} Leads`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Sequence Enrollment Dialog */}
      <Dialog open={sequenceDialogOpen} onOpenChange={setSequenceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-blue-600" />
              Enroll in Sequence
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Enroll <strong>{selectedIds.size}</strong> selected leads in a multi-channel sequence.
            </p>
            
            {sequences.length > 0 ? (
              <div className="space-y-2">
                <Label>Select Sequence *</Label>
                <Select value={selectedSequenceId} onValueChange={setSelectedSequenceId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an active sequence" />
                  </SelectTrigger>
                  <SelectContent>
                    {sequences.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        <div className="flex items-center gap-2">
                          <span>{s.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {(s.steps || []).length} steps
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {selectedSequenceId && sequences.find(s => s.id === selectedSequenceId) && (
                  <div className="p-3 bg-blue-50 rounded-lg text-xs text-blue-800">
                    <p><strong>Sequence:</strong> {sequences.find(s => s.id === selectedSequenceId)?.name}</p>
                    <p className="mt-1">
                      Steps: {(sequences.find(s => s.id === selectedSequenceId)?.steps || [])
                        .map(step => step.name)
                        .join(' → ')}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <strong>No active sequences</strong>
                <p className="mt-1">Create and activate a sequence in the Sequences page before enrolling leads.</p>
              </div>
            )}
            
            <div className="p-3 bg-slate-50 rounded-lg text-xs text-slate-600">
              <p><strong>Note:</strong> Leads already enrolled in this sequence will be skipped. Each lead can only be enrolled once per sequence.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSequenceDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleEnrollInSequence} 
              disabled={!selectedSequenceId || sequenceEnrollLoading || sequences.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sequenceEnrollLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enrolling...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-2" />
                  Enroll Leads
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>)}
        </TabsContent>
      </Tabs>

      {/* Create Email List Dialog */}
      <Dialog open={emailListDialogOpen} onOpenChange={setEmailListDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Email List from Filtered Leads</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Create email list from <span className="font-semibold text-indigo-600">{filteredLeadsWithEmail.length}</span> filtered leads with email
              {effectiveFilteredLeads.length - filteredLeadsWithEmail.length > 0 && (
                <span className="text-slate-400"> ({effectiveFilteredLeads.length - filteredLeadsWithEmail.length} skipped — no email)</span>
              )}
            </p>
            <div className="space-y-2">
              <Label htmlFor="emailListName">List Name *</Label>
              <Input
                id="emailListName"
                value={emailListName}
                onChange={e => setEmailListName(e.target.value)}
                placeholder="e.g. Q2 Recruiters — Northeast"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emailListDesc">Description</Label>
              <Textarea
                id="emailListDesc"
                value={emailListDescription}
                onChange={e => setEmailListDescription(e.target.value)}
                placeholder="Optional notes about this list..."
                rows={3}
              />
            </div>
            <p className="text-xs text-slate-400">Contacts will be deduplicated and email addresses auto-verified.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailListDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreateEmailList}
              disabled={emailListCreating || !emailListName.trim() || filteredLeadsWithEmail.length === 0}
            >
              {emailListCreating ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</>
              ) : (
                <><ListPlus className="h-4 w-4 mr-2" />Create List</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
